import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ScreenShell } from '../shell/ScreenShell';
import { Button } from '../design-system/Button';
import { Text } from '../design-system/Text';
import { NudgePostPaymentCard } from '../nudges/NudgePostPaymentCard';
import { useSessionStore } from '../../stores/sessionStore';
import { usePrototypeStore } from '../../stores/prototypeStore';
import { analytics } from '../../lib/analytics';
import { defaultVehicle } from '../../fixtures/vehicles';
import { fastagProfiles } from '../../fixtures/fastagProfiles';
import { computeRecommendedAmount } from '../../utils/qualification';
import { CheckCircle, Share2 } from 'lucide-react';
import type { FastagProfile } from '../../types';

export function PaymentSuccessScreen() {
  const navigate = useNavigate();
  const completePayment = useSessionStore((s) => s.completePayment);
  const paymentStatus = useSessionStore((s) => s.paymentStatus);
  const selectedIds = useSessionStore((s) => s.selectedChallanIds);
  const dismissed = useSessionStore((s) => s.dismissedNudgeIds);
  const dismissNudge = useSessionStore((s) => s.dismissNudge);
  const startRecharge = useSessionStore((s) => s.startRecharge);
  const resetPayment = useSessionStore((s) => s.resetPayment);
  const clearSelection = useSessionStore((s) => s.clearSelection);

  const qualificationState = usePrototypeStore((s) => s.qualificationState);
  const balanceValue = usePrototypeStore((s) => s.balanceValue);
  const activeVariant = usePrototypeStore((s) => s.activeVariant);

  useEffect(() => {
    if (paymentStatus !== 'success') {
      completePayment();
    }
    analytics.emit('payment_success', {
      total: 0, // placeholder; computed below
      count: selectedIds.length,
    });
  }, []);

  const baseProfile: FastagProfile = fastagProfiles[qualificationState] ?? fastagProfiles.low;
  const profile: FastagProfile = {
    ...baseProfile,
    balance: balanceValue ?? baseProfile.balance,
    recommendedAmount: computeRecommendedAmount(balanceValue ?? baseProfile.balance),
  };

  const showPostPaymentNudge =
    (activeVariant === 'recommended' || activeVariant === 'F') &&
    qualificationState !== 'unqualified';

  const goToRecharge = () => {
    analytics.emit('recharge_started', {
      source: 'post_payment_card',
      balance: profile.balance,
      recommended_amount: profile.recommendedAmount,
    });
    startRecharge('post_payment_card');
    const params = new URLSearchParams({
      source: 'post_payment_card',
      returnTo: '/payment-success',
      balance: String(profile.balance),
      recommended_amount: String(profile.recommendedAmount),
    });
    navigate(`/fastag-recharge?${params.toString()}`);
  };

  const backToHome = () => {
    resetPayment();
    clearSelection();
    navigate('/');
  };

  return (
    <ScreenShell title="Payment Successful" showBack={false}>
      <div className="flex flex-col items-center px-6 pt-8 text-center">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 260, damping: 20 }}
          className="flex h-20 w-20 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400"
          role="alert"
          aria-label="Payment successful"
        >
          <CheckCircle className="h-10 w-10" />
        </motion.div>

        <Text variant="display-medium" className="mt-5">
          Payment successful
        </Text>
        <Text variant="body" color="secondary" className="mt-1">
          Your challans have been cleared.
        </Text>

        <div className="mt-6 w-full rounded-xl border border-slate-100 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
          <Text variant="caption" color="secondary">
            Reference
          </Text>
          <Text variant="body-medium">PARK{Date.now().toString().slice(-8)}</Text>
          <div className="mt-3 flex items-center justify-between">
            <Text variant="caption" color="secondary">
              Vehicle
            </Text>
            <Text variant="body-medium">{defaultVehicle.number}</Text>
          </div>
          <div className="mt-1 flex items-center justify-between">
            <Text variant="caption" color="secondary">
              Paid at
            </Text>
            <Text variant="body-medium">
              {new Date().toLocaleString('en-IN', {
                dateStyle: 'medium',
                timeStyle: 'short',
              })}
            </Text>
          </div>
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-3 px-4">
        {showPostPaymentNudge && (
          <NudgePostPaymentCard
            profile={profile}
            visible={true}
            dismissed={dismissed.includes('nudge_success')}
            onRecharge={goToRecharge}
            onDismiss={() => {
              analytics.emit('nudge_dismiss', { nudge_id: 'nudge_success' });
              dismissNudge('nudge_success');
            }}
          />
        )}
      </div>

      <div className="mt-auto p-4 pb-6">
        <Button variant="outline" fullWidth className="mb-3">
          <Share2 className="h-4 w-4" />
          Share Receipt
        </Button>
        <Button fullWidth onClick={backToHome}>
          Back to Home
        </Button>
      </div>
    </ScreenShell>
  );
}
